import React, { useEffect, useState } from "react";
import { Box, Flex, Spinner, Badge } from "theme-ui";
import {
  getPendingProviderInvoices,
  approveProviderInvoice,
  rejectProviderInvoice,
  getProviderInvoiceFileUrl,
} from "api/ProviderInvoice";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import TableBasic from "components/ui/Table";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import { themevals } from "theme/themevals";
import { useToast } from "components/toast";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import ApprovePanel from "./ApprovePanel";

const ProviderInvoiceReview: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<any[] | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showVarianceModal, setShowVarianceModal] = useState(false);
  const [varianceInvoice, setVarianceInvoice] = useState<any | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveInvoice, setApproveInvoice] = useState<any | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  // Approve opens the review panel: PO lines, charges, Zoho bill + payments (2026-10-20)
  const [panelInvoice, setPanelInvoice] = useState<any | null>(null);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [filePreviewName, setFilePreviewName] = useState<string>("");
  const [filePreviewLoading, setFilePreviewLoading] = useState(false);

  // Editable fields for email-sourced invoices (keyed by invoice ID)
  // Use a ref to avoid re-renders that steal focus from inputs
  const editedFieldsRef = React.useRef<Record<string, { invoiceNumber?: string; amount?: string }>>({});
  const [editedFieldsVersion, setEditedFieldsVersion] = useState(0);

  const columnHelper = createColumnHelper();

  const loadInvoices = async () => {
    try {
      const response = await getPendingProviderInvoices();
      if (response.success) {
        setInvoices(response.data || []);
      } else {
        setInvoices([]);
        toast.add(response.error || "Failed to load invoices", themevals.colors.danger);
      }
    } catch (e: any) {
      console.error("Failed to load pending invoices", e);
      setInvoices([]);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const isEmailSource = (invoice: any) => invoice?.source === "email";

  const getEditedInvoiceNumber = (invoice: any) =>
    editedFieldsRef.current[invoice.id]?.invoiceNumber ?? invoice.invoiceNumber ?? "";

  const getEditedAmount = (invoice: any) =>
    editedFieldsRef.current[invoice.id]?.amount ?? (invoice.amount != null ? String(invoice.amount) : "");

  const updateEditedField = (invoiceId: string, field: "invoiceNumber" | "amount", value: string) => {
    editedFieldsRef.current = {
      ...editedFieldsRef.current,
      [invoiceId]: { ...editedFieldsRef.current[invoiceId], [field]: value },
    };
  };

  // Check if an email-sourced invoice has required fields filled in
  const canApproveEmailInvoice = (invoice: any) => {
    if (!isEmailSource(invoice)) return true;
    const invNum = getEditedInvoiceNumber(invoice);
    const amt = getEditedAmount(invoice);
    return invNum.trim().length > 0 && amt.trim().length > 0 && !isNaN(Number(amt));
  };

  const getApproveOptions = (invoice: any) => {
    // Fill in a blank OR correct an AI misread: send what differs.
    const opts: any = {};
    const editedNum = editedFieldsRef.current[invoice.id]?.invoiceNumber?.trim();
    const editedAmt = editedFieldsRef.current[invoice.id]?.amount?.trim();
    if (editedNum && editedNum !== String(invoice.invoiceNumber ?? "").trim()) opts.invoiceNumber = editedNum;
    if (editedAmt && !isNaN(Number(editedAmt)) && Number(editedAmt) !== Number(invoice.amount ?? NaN)) opts.amount = Number(editedAmt);
    return opts;
  };

  const handleApprove = async (invoice: any, approveWithVariance?: boolean) => {
    if (!approveWithVariance) {
      // Check email invoice has required fields
      if (!canApproveEmailInvoice(invoice)) {
        toast.add("Please fill in Invoice # and Amount before approving.", themevals.colors.danger);
        return;
      }

      const pct = Math.abs(Number(invoice.variancePct || 0));
      if (pct > 5) {
        setVarianceInvoice(invoice);
        setShowVarianceModal(true);
        return;
      }

      setApproveInvoice(invoice);
      setShowApproveModal(true);
      return;
    }

    setProcessing(invoice.id);
    try {
      const response = await approveProviderInvoice(invoice.id, {
        approveWithVariance: approveWithVariance || false,
        ...getApproveOptions(invoice),
      });

      if (response.error === "variance_requires_confirmation") {
        setVarianceInvoice({
          ...invoice,
          amount: response.data?.amount ?? invoice.amount,
          varianceAmount: response.data?.varianceAmount ?? invoice.varianceAmount,
          variancePct: response.data?.variancePct ?? invoice.variancePct,
        });
        setShowVarianceModal(true);
        setProcessing(null);
        return;
      }

      if (response.success) {
        toast.add(
          `Invoice approved! Bill ${response.billId || ""} created in Zoho.`,
          themevals.colors.success
        );
        setShowVarianceModal(false);
        setVarianceInvoice(null);
        loadInvoices();
      } else {
        toast.add(response.error || "Failed to approve invoice", themevals.colors.danger);
      }
    } catch (e: any) {
      toast.add(e?.message || "Error approving invoice", themevals.colors.danger);
    }
    setProcessing(null);
  };

  const openApprovePanel = (invoice: any) => {
    if (!canApproveEmailInvoice(invoice)) {
      toast.add("Please fill in Invoice # and Amount before approving.", themevals.colors.danger);
      return;
    }
    setPanelInvoice(invoice);
  };

  const onPanelApproved = (response: any) => {
    setPanelInvoice(null);
    toast.add(`Invoice approved. Zoho bill ${response.billId || ""} is up to date.`, themevals.colors.success);
    (response.warnings || []).forEach((w: string) => toast.add(w, themevals.colors.warning || themevals.colors.danger));
    loadInvoices();
  };

  const handleApproveWithVariance = async () => {
    if (!varianceInvoice) return;
    await handleApprove(varianceInvoice, true);
  };

  const handleConfirmApprove = async () => {
    if (!approveInvoice) return;
    setShowApproveModal(false);
    setProcessing(approveInvoice.id);
    try {
      const response = await approveProviderInvoice(approveInvoice.id, {
        approveWithVariance: false,
        ...getApproveOptions(approveInvoice),
      });

      if (response.error === "variance_requires_confirmation") {
        setVarianceInvoice({
          ...approveInvoice,
          amount: response.data?.amount ?? approveInvoice.amount,
          varianceAmount: response.data?.varianceAmount ?? approveInvoice.varianceAmount,
          variancePct: response.data?.variancePct ?? approveInvoice.variancePct,
        });
        setShowVarianceModal(true);
        setProcessing(null);
        setApproveInvoice(null);
        return;
      }

      if (response.success) {
        toast.add(
          `Invoice approved! Bill ${response.billId || ""} created in Zoho.`,
          themevals.colors.success
        );
        loadInvoices();
      } else {
        toast.add(response.error || "Failed to approve invoice", themevals.colors.danger);
      }
    } catch (e: any) {
      toast.add(e?.message || "Error approving invoice", themevals.colors.danger);
    }
    setProcessing(null);
    setApproveInvoice(null);
  };

  const openRejectModal = (invoice: any) => {
    setSelectedInvoice(invoice);
    setRejectNotes("");
    setShowRejectModal(true);
  };

  const openFilePreview = async (invoice: any) => {
    if (!invoice.id) return;
    setFilePreviewName(invoice.fileName || "Invoice File");
    setFilePreviewLoading(true);
    setShowFilePreview(true);
    setFilePreviewUrl(null);
    try {
      const res = await getProviderInvoiceFileUrl(invoice.id);
      if (res.success && res.url) {
        setFilePreviewUrl(res.url);
      } else {
        toast.add(res.error || "Unable to load file", themevals.colors.danger);
        setShowFilePreview(false);
      }
    } catch (e: any) {
      toast.add("Error loading file preview", themevals.colors.danger);
      setShowFilePreview(false);
    }
    setFilePreviewLoading(false);
  };

  const isImageFile = (name: string) => {
    const ext = name.toLowerCase().split(".").pop() || "";
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  };

  const handleReject = async () => {
    if (!selectedInvoice) return;

    setProcessing(selectedInvoice.id);
    try {
      const response = await rejectProviderInvoice(selectedInvoice.id, rejectNotes);
      if (response.success) {
        toast.add("Invoice rejected.", themevals.colors.success);
        setShowRejectModal(false);
        setSelectedInvoice(null);
        loadInvoices();
      } else {
        toast.add(response.error || "Failed to reject invoice", themevals.colors.danger);
      }
    } catch (e: any) {
      toast.add(e?.message || "Error rejecting invoice", themevals.colors.danger);
    }
    setProcessing(null);
  };

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "pending_approval":
        return <Badge as="span" variant="info">Pending</Badge>;
      case "review_needed":
        return <Badge as="span" variant="warning">Review Needed</Badge>;
      case "revision_pending":
        return <Badge as="span" variant="warning">Revision</Badge>;
      default:
        return <Badge as="span" variant="ltgrey">{status}</Badge>;
    }
  };

  const renderSourceBadge = (row: any) => {
    if (row.source === "email") {
      const verified = row.senderVerified !== false;
      return (
        <span style={{ fontSize: "12px", color: verified ? themevals.colors.dkgrey : '#D97706' }}
          title={row.senderEmail
            ? `Email from ${row.senderEmail}${!verified ? ' (unverified sender domain)' : ''}`
            : "Email submission"}
        >
          Email{!verified && " \u26A0"}
        </span>
      );
    }
    return (
      <span style={{ fontSize: "12px", color: themevals.colors.dkgrey }}>Portal</span>
    );
  };

  const renderVariance = (row: any) => {
    if (row.variancePct == null) return "\u2014";
    const pct = Number(row.variancePct);
    const amt = Number(row.varianceAmount);
    const color = Math.abs(pct) > 5 ? themevals.colors.danger : themevals.colors.dkgrey;
    const sign = amt >= 0 ? "+" : "";
    return (
      <span style={{ color }}>
        {sign}${amt.toFixed(2)} ({sign}{pct.toFixed(1)}%)
      </span>
    );
  };

  /** Render a small 'AI' tag next to OCR-extracted values */
  const AiTag = ({ confidence }: { confidence?: string }) => (
    <span
      title={`AI-extracted (${confidence || "unknown"} confidence) \u2014 please verify`}
      style={{
        display: "inline-block",
        fontSize: "9px",
        fontWeight: 600,
        color: "#7C3AED",
        backgroundColor: "#EDE9FE",
        borderRadius: "3px",
        padding: "1px 4px",
        marginLeft: "6px",
        verticalAlign: "middle",
        lineHeight: "14px",
        letterSpacing: "0.5px",
      }}
    >
      AI
    </span>
  );

  const columns = [
    columnHelper.accessor((row: any) => row, {
      id: "source",
      cell: (info: any) => renderSourceBadge(info.getValue()),
      header: () => <span>Source</span>,
      size: 70,
    }),
    columnHelper.accessor((row: any) => row?.providerName, {
      id: "providerName",
      cell: (info: any) => {
        const row = info.row.original;
        return (
          <Box>
            <Box>{info.getValue() || "\u2014"}</Box>
            {row.senderEmail && row.source === "email" && (
              <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey }}>
                {row.senderEmail}
              </Box>
            )}
          </Box>
        );
      },
      header: () => <span>Provider</span>,
    }),
    columnHelper.accessor((row: any) => row?.purchaseOrderSequence, {
      id: "poNumber",
      cell: (info: any) => {
        const row = info.row.original;
        const seq = info.getValue();
        const poNum = seq ? `GMO-${String(seq).padStart(5, "0")}` : "\u2014";
        return (
          <Box
            sx={{ color: themevals.colors.primary, cursor: "pointer", textDecoration: "underline" }}
            onClick={() => row.purchaseOrderId && navigate(`/purchase-orders/${row.purchaseOrderId}`)}
          >
            {poNum}
          </Box>
        );
      },
      header: () => <span>PO #</span>,
    }),
    columnHelper.accessor((row: any) => row, {
      id: "invoiceNumber",
      cell: (info: any) => {
        const row = info.getValue();
        // Editable on every invoice in this queue (fill in or correct).
        if (row) {
          return (
            <Flex sx={{ alignItems: "center", gap: "4px" }}>
            <input
              type="text"
              placeholder="Enter invoice #"
              defaultValue={getEditedInvoiceNumber(row)}
              onBlur={(e) => updateEditedField(row.id, "invoiceNumber", e.target.value)}
              onChange={(e) => updateEditedField(row.id, "invoiceNumber", e.target.value)}
              style={{
                width: "110px",
                padding: "4px 6px",
                fontSize: "12px",
                border: `1px solid ${themevals.colors.inputOutline}`,
                borderRadius: "4px",
              }}
            />
            {row.extracted?.invoiceNumber && <AiTag confidence={row.extracted.confidence} />}
            </Flex>
          );
        }
        return "\u2014";
      },
      header: () => <span>Invoice #</span>,
    }),
    columnHelper.accessor((row: any) => row, {
      id: "amount",
      cell: (info: any) => {
        const row = info.getValue();
        // Editable on every invoice in this queue (fill in or correct).
        if (row) {
          return (
            <Flex sx={{ alignItems: "center", gap: "2px" }}>
              <span style={{ fontSize: "12px" }}>$</span>
              <input
                type="text"
                placeholder="0.00"
                defaultValue={getEditedAmount(row)}
                onBlur={(e) => {
                  const val = e.target.value.replace(/[^0-9.]/g, "");
                  updateEditedField(row.id, "amount", val);
                }}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.]/g, "");
                  updateEditedField(row.id, "amount", val);
                }}
                style={{
                  width: "80px",
                  padding: "4px 6px",
                  fontSize: "12px",
                  border: `1px solid ${themevals.colors.inputOutline}`,
                  borderRadius: "4px",
                }}
              />
              {row.extracted?.amount != null && <AiTag confidence={row.extracted.confidence} />}
            </Flex>
          );
        }
        return "\u2014";
      },
      header: () => <span>Amount</span>,
    }),
    columnHelper.accessor((row: any) => row, {
      id: "variance",
      cell: (info: any) => renderVariance(info.getValue()),
      header: () => <span>Variance</span>,
    }),
    columnHelper.accessor((row: any) => row?.fileName, {
      id: "fileName",
      cell: (info: any) => {
        const row = info.row.original;
        const name = info.getValue();
        return (
          <Box>
            {name ? (
              <Box
                sx={{ color: themevals.colors.primary, cursor: "pointer", textDecoration: "underline", fontSize: "12px" }}
                onClick={() => openFilePreview(row)}
              >
                {name}
              </Box>
            ) : null}
            {row.invoiceUrl && (
              <a
                href={row.invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: "11px", color: themevals.colors.primary, display: "block", marginTop: "2px" }}
              >
                Invoice Link &#8599;
              </a>
            )}
          </Box>
        );
      },
      header: () => <span>File</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => renderStatusBadge(info.getValue()),
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.createdAt, {
      id: "createdAt",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY h:mm A") : ""),
      header: () => <span>Submitted</span>,
    }),
    columnHelper.accessor((row: any) => row, {
      id: "actions",
      cell: (info: any) => {
        const row = info.getValue();
        const isProcessing = processing === row.id;
        return (
          <Flex sx={{ gap: "8px" }}>
            <BasicButton
              scheme="primary"
              onClick={() => openApprovePanel(row)}
              disabled={isProcessing}
              styles={{ padding: "4px 12px", fontSize: "12px" }}
            >
              {isProcessing ? "..." : "Approve"}
            </BasicButton>
            <BasicButton
              scheme="secondaryOutline"
              onClick={() => openRejectModal(row)}
              disabled={isProcessing}
              styles={{ padding: "4px 12px", fontSize: "12px" }}
            >
              Reject
            </BasicButton>
          </Flex>
        );
      },
      header: () => <span>Actions</span>,
    }),
  ];

  return (
    <Box sx={{ padding: "30px" }}>
      <Flex sx={{ justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <H5 fontFamily="Rubik" fontSize="20px" marginLeft="0px" marginTop="0px">
          Provider Invoice Review
        </H5>
        <BasicButton scheme="secondaryOutline" onClick={loadInvoices}>
          Refresh
        </BasicButton>
      </Flex>

      {invoices === null ? (
        <Flex sx={{ justifyContent: "center", alignItems: "center", width: "100%", minHeight: "200px" }}>
          <Spinner size={32} />
        </Flex>
      ) : invoices.length > 0 ? (
        <TableBasic
          data={invoices}
          columns={columns}
          enablePaging={invoices.length > 20}
          enableSearch={false}
          customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }}
          noFirstColumnMinWidth={true}
          size="sm"
        />
      ) : (
        <Flex
          sx={{
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            minHeight: "200px",
            backgroundColor: themevals.colors.ltgrey,
            borderRadius: "8px",
          }}
        >
          <P fontFamily="Poppins" fontSize="14px" color="gray">
            No pending invoices to review.
          </P>
        </Flex>
      )}

      {panelInvoice && (
        <ApprovePanel
          invoice={panelInvoice}
          invoiceNumber={getEditedInvoiceNumber(panelInvoice)}
          invoiceAmount={Number(getEditedAmount(panelInvoice) || panelInvoice.amount || 0)}
          extraOptions={getApproveOptions(panelInvoice)}
          onClose={() => setPanelInvoice(null)}
          onApproved={onPanelApproved}
          onPreview={() => openFilePreview(panelInvoice)}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedInvoice && (
        <Modal isOpen={showRejectModal} toggle={() => setShowRejectModal(false)} size="sm" centered>
          <ModalHeader toggle={() => setShowRejectModal(false)}>
            Reject Invoice
          </ModalHeader>
          <ModalBody>
            <P marginBottom="10px">
              Rejecting invoice <strong>{selectedInvoice.invoiceNumber || selectedInvoice.id.slice(0, 8)}</strong> from{" "}
              <strong>{selectedInvoice.providerName || "provider"}</strong>.
            </P>
            <P fontSize="13px" color={themevals.colors.dkgrey} marginBottom="16px">
              The provider will see your rejection reason and can resubmit.
            </P>
            <TextInput
              label="Reason for rejection"
              name="rejectNotes"
              type="textarea"
              value={rejectNotes}
              onChange={(val: any) => setRejectNotes(val || "")}
              placeholder="e.g. Amount doesn't match PO, wrong PO number, missing line items..."
              rows={3}
            />
          </ModalBody>
          <ModalFooter>
            <BasicButton scheme="secondaryOutline" onClick={() => setShowRejectModal(false)}>
              Cancel
            </BasicButton>
            <BasicButton
              scheme="danger"
              onClick={handleReject}
              disabled={processing === selectedInvoice.id}
            >
              {processing === selectedInvoice.id ? "Rejecting..." : "Reject Invoice"}
            </BasicButton>
          </ModalFooter>
        </Modal>
      )}

      {/* Approve Confirmation Modal (zero-variance) */}
      {showApproveModal && approveInvoice && (
        <Modal isOpen={showApproveModal} toggle={() => { setShowApproveModal(false); setApproveInvoice(null); }} size="sm" centered>
          <ModalHeader toggle={() => { setShowApproveModal(false); setApproveInvoice(null); }}>
            Approve Invoice
          </ModalHeader>
          <ModalBody>
            <P marginBottom="10px">
              Approve invoice <strong>{getEditedInvoiceNumber(approveInvoice) || approveInvoice.id.slice(0, 8)}</strong> from{" "}
              <strong>{approveInvoice.providerName || "provider"}</strong> for{" "}
              <strong>${Number(getEditedAmount(approveInvoice) || approveInvoice.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong>?
            </P>
            <P fontSize="13px" color={themevals.colors.dkgrey}>
              This will create a bill in Zoho Books and mark the PO as billed.
            </P>
            {isEmailSource(approveInvoice) && (
              <Box sx={{
                backgroundColor: "#E8F0FE",
                border: "1px solid #C4D8F0",
                borderRadius: "6px",
                padding: "10px 12px",
                marginTop: "12px",
              }}>
                <P fontSize="12px" color="#1A56B8">
                  Email submission &mdash; Invoice # and amount will be saved to the record.
                </P>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <BasicButton scheme="secondaryOutline" onClick={() => { setShowApproveModal(false); setApproveInvoice(null); }}>
              Cancel
            </BasicButton>
            <BasicButton
              scheme="primary"
              onClick={handleConfirmApprove}
              disabled={processing === approveInvoice.id}
            >
              {processing === approveInvoice.id ? "Approving..." : "Approve"}
            </BasicButton>
          </ModalFooter>
        </Modal>
      )}

      {/* Variance Confirmation Modal */}
      {showVarianceModal && varianceInvoice && (
        <Modal isOpen={showVarianceModal} toggle={() => { setShowVarianceModal(false); setVarianceInvoice(null); }} size="md" centered>
          <ModalHeader toggle={() => { setShowVarianceModal(false); setVarianceInvoice(null); }}>
            Approve with Variance
          </ModalHeader>
          <ModalBody>
            <Box sx={{
              backgroundColor: "#FFF3CD",
              border: "1px solid #FFEEBA",
              borderRadius: "6px",
              padding: "16px",
              marginBottom: "16px",
            }}>
              <P fontFamily="Poppins" fontSize="14px" fontWeight="600" marginBottom="8px" color="#856404">
                Invoice amount doesn&apos;t match PO total
              </P>
              <P fontSize="13px" color="#856404">
                This invoice is{" "}
                <strong>
                  ${Math.abs(Number(varianceInvoice.varianceAmount || 0)).toFixed(2)}{" "}
                  {Number(varianceInvoice.varianceAmount || 0) >= 0 ? "over" : "under"}
                </strong>
                {" "}the PO total ({Number(varianceInvoice.variancePct || 0) >= 0 ? "+" : ""}
                {Number(varianceInvoice.variancePct || 0).toFixed(1)}%).
              </P>
            </Box>
            <Flex sx={{ justifyContent: "space-between", marginBottom: "8px" }}>
              <P fontSize="13px" color={themevals.colors.dkgrey}>Invoice amount:</P>
              <P fontSize="13px" fontWeight="600">${Number(varianceInvoice.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</P>
            </Flex>
            <Flex sx={{ justifyContent: "space-between", marginBottom: "16px" }}>
              <P fontSize="13px" color={themevals.colors.dkgrey}>Current PO total:</P>
              <P fontSize="13px" fontWeight="600">
                ${(Number(varianceInvoice.amount || 0) - Number(varianceInvoice.varianceAmount || 0)).toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </P>
            </Flex>
            <P fontSize="13px" color={themevals.colors.dkgrey} marginBottom="8px">
              Approving will add a &ldquo;Change order adjustment&rdquo; line item to the PO for{" "}
              <strong>${Number(varianceInvoice.varianceAmount || 0).toFixed(2)}</strong>,
              update the PO total to match the invoice amount, and create the Zoho bill.
            </P>
            <P fontSize="12px" color={themevals.colors.dkgrey} fontStyle="italic">
              This typically reflects a change order that hasn&apos;t been updated on the PO yet.
              If the invoice amount is wrong, reject it instead.
            </P>
          </ModalBody>
          <ModalFooter>
            <BasicButton scheme="secondaryOutline" onClick={() => { setShowVarianceModal(false); setVarianceInvoice(null); }}>
              Cancel
            </BasicButton>
            <BasicButton
              scheme="danger"
              onClick={() => openRejectModal(varianceInvoice)}
              styles={{ marginRight: "auto" }}
            >
              Reject Instead
            </BasicButton>
            <BasicButton
              scheme="primary"
              onClick={handleApproveWithVariance}
              disabled={processing === varianceInvoice.id}
            >
              {processing === varianceInvoice.id ? "Approving..." : "Approve & Update PO"}
            </BasicButton>
          </ModalFooter>
        </Modal>
      )}

      {/* File Preview Modal */}
      {showFilePreview && (
        <Modal isOpen={showFilePreview} toggle={() => { setShowFilePreview(false); setFilePreviewUrl(null); }} size="lg" centered>
          <ModalHeader toggle={() => { setShowFilePreview(false); setFilePreviewUrl(null); }}>
            {filePreviewName}
          </ModalHeader>
          <ModalBody>
            {filePreviewLoading && (
              <Flex sx={{ justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                <Spinner size={32} />
              </Flex>
            )}
            {!filePreviewLoading && filePreviewUrl && (
              isImageFile(filePreviewName) ? (
                <Flex sx={{ justifyContent: "center" }}>
                  <img
                    src={filePreviewUrl}
                    alt={filePreviewName}
                    style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: "4px" }}
                  />
                </Flex>
              ) : (
                <iframe
                  src={filePreviewUrl}
                  title={filePreviewName}
                  style={{ width: "100%", height: "70vh", border: "none", borderRadius: "4px" }}
                />
              )
            )}
            {!filePreviewLoading && !filePreviewUrl && (
              <Flex sx={{ justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                <P fontFamily="Poppins" fontSize="14px" color={themevals.colors.dkgrey}>Unable to load file preview.</P>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            {filePreviewUrl && (
              <BasicButton
                scheme="secondaryOutline"
                onClick={() => window.open(filePreviewUrl, "_blank")}
              >
                Open in New Tab
              </BasicButton>
            )}
            <BasicButton scheme="secondaryOutline" onClick={() => { setShowFilePreview(false); setFilePreviewUrl(null); }}>
              Close
            </BasicButton>
          </ModalFooter>
        </Modal>
      )}
    </Box>
  );
};

export default ProviderInvoiceReview;
